/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { formatNumber, showToast, TOAST_TYPE } from '@utils/utils'
import { isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import _ from 'lodash'
import { BET_MONEY_TYPE } from 'adv-protocol/src/GameCommon'
import { FriendInvitationRewardReceivedStatus } from '@interface/common'
// img
import { Button } from '@styles/button.style'
import { DiaIco, GmoneyIco } from '@shared/Icon/MoneyIcon'
// css
import { RewardListWrap } from '../index.style'

interface FriendInvitationRewardReceivedStatusExt extends FriendInvitationRewardReceivedStatus {
  percent: number
}

const RewardList: React.FC = () => {
  const { componentStore } = useRootStore()
  const { friendInvitationInvitor } = componentStore

  const [rewards, setRewards] = useState([])
  const [bettingMoneySummary, setBettingMoneySummary] = useState(0)

  useEffect(() => {
    if (_.isEmpty(friendInvitationInvitor)) {
      return
    }

    const summary = _.toNumber(
      _.find(friendInvitationInvitor?.invitedUsersBettingMoneySummary, targetObject => {
        return _.toNumber(targetObject?.betMoneyType) === _.toNumber(friendInvitationInvitor.betMoneyType)
      })?.membershipClubBettingMoney || 0
    )
    setBettingMoneySummary(summary)
    setRewards(makeRewards(friendInvitationInvitor.rewardReceivedStates, summary))
  }, [friendInvitationInvitor])

  const makeRewards = (statuses: FriendInvitationRewardReceivedStatus[], paramSummary: number) => {
    const result: FriendInvitationRewardReceivedStatusExt[] = []
    let beforeStandard = 0
    _.forEach(statuses, status => {
      result.push({
        ...status,
        percent: calculateSectionPercent(beforeStandard, status.standard, paramSummary),
      })

      beforeStandard = status.standard
    })
    return result
  }

  const calculateSectionPercent = (beforeStandard: number, currentStandard: number, paramSummary: number) => {
    let percent: number
    if (currentStandard <= paramSummary) {
      percent = 100
    } else {
      const sectionValue = paramSummary - beforeStandard
      const range = currentStandard - beforeStandard
      percent = (sectionValue / range) * 100 + 0.01
      if (percent > 100) {
        percent = 100
      } else if (percent < 0) {
        percent = 0
      }
    }
    return percent
  }

  const validateRewardReceive = (reward: FriendInvitationRewardReceivedStatusExt) => {
    if (reward.isReceived) {
      return false
    }

    if (bettingMoneySummary < reward.standard) {
      return false
    }

    return true
  }

  const onClickRewardReceive = async (reward: FriendInvitationRewardReceivedStatusExt) => {
    if (!validateRewardReceive(reward)) {
      return
    }

    try {
      const result = await componentStore.api.component.receiveFriendInvitationReward({
        rewardId: reward.id, targetAt: friendInvitationInvitor.startOfMonth
      })
      if (result.result) {
        showToast(TOAST_TYPE.INFO, '보상이 메시지함으로 지급되었습니다.')
        setIsReceivedInReward(reward.id, true)
      } else {
        showToast(TOAST_TYPE.ERROR, '보상 수령에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      }
    } catch (err) {
      showToast(TOAST_TYPE.ERROR, '보상 수령에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      console.error(err)
    }
  }

  const setIsReceivedInReward = (rewardId: string, isReceived: boolean) => {
    const cloneRewards = _.cloneDeep(rewards)
    setRewards([])

    const foundReward = _.find(cloneRewards, reward => reward.id === rewardId)
    foundReward.isReceived = isReceived
    setRewards(cloneRewards)
  }

  return (
    <RewardListWrap className={isMobile && 'm-reward-list-wrap'}>
      <ul className="reward-list">
        {rewards.map((reward) => {
          const key = `FriendsInvitation:RewardList:${reward.standard}`
          return (
              <li key={key} className={`reward-list--item ${reward.standard <= bettingMoneySummary && 'arrival'} ${reward.isReceived && 'complete'}`}>
                <span className="range">{reward.standardName}</span>

                {/* 구간별 보상 영역 */}
                <Button
                  className="reward-box"
                  onClick={() => {
                    onClickRewardReceive(reward)
                  }}
                >
                  {reward.rewardName}
                  <span className="reward-btn">수령</span>
                  <span className="check-ico"/>
                </Button>

                {/* 게이지 바 및 나의 현재 위치 */}
                <span className="gauge-bar" style={{ width: `${reward.percent}%` }}>
                {reward.percent > 0 &&
                  <div className="my-gauge">
                    {formatNumber(bettingMoneySummary)}
                    {_.toNumber(friendInvitationInvitor?.betMoneyType) === BET_MONEY_TYPE.G_MONEY
                        ? <GmoneyIco/> : <DiaIco/>}
                    <p className="gauge-txt">친구들의 베팅 총액</p>
                  </div>
                }
              </span>

                {/* 금액 구간 달성 시 연출 */}
                <span className="circle">
                <span className="pulse-ani"/>
                <span className="pulse-ani ani-2"/>
                <span className="pulse-ani ani-3"/>
              </span>
            </li>
          )
        }
        )}
      </ul>
    </RewardListWrap>
  )
}

export default observer(RewardList)
